import { createContext, useRef } from "react";

import type { MapViewApi } from "@map/mapiq-map-react/src/MapView";
import {
  type HereAndNowTab,
  type UsageStatsType,
  useBuildingResourcesLoader,
  useBuildingWorkspaceReservations,
  useHaNFloor,
  useHaNMobileView,
  useHereAndNowFilterData,
  useOccupancyLoader,
  useRefreshBucket,
  useRoomsTimeWindow,
  useTypologyLoader,
  useUsageStats,
} from "@lib/logic";
import {
  type FacilityResource,
  getBuildingResources,
  getIsOccupancyEnabled,
  getWorkspaceOccupancyForBuildingId,
  type Resource,
  useAppSelector,
} from "@lib/store";
import { useFeatureToggle } from "@hooks";
import { getDeskAmenityIcon, getRoomEquipementIcon } from "@utils";

import { useHaNBuilding } from "../data/useHaNBuilding";
import { refreshClock } from "../state/refresh/refreshClock";
import { haNSession } from "../state/session/haNSession";
import { useAvailableTabs } from "../useHaNTabs";

const NO_RESOURCES: Resource[] = [];

const isFacility = (resource: Resource): resource is FacilityResource => {
  return resource.type === "facility";
};

const USAGE_STATS_TYPE_BY_TAB: Record<HereAndNowTab, UsageStatsType> = {
  rooms: "rooms",
  workspaces: "workspaces",
  people: "none",
  facilities: "none",
};

type HaNContextOptions = {
  isSearching: boolean;
};

/**
 * Everything H&N shows for the current building and floor.
 * Loads what's missing or stale.
 */
export const useHaNContextValue = (tab: HereAndNowTab, { isSearching }: HaNContextOptions) => {
  const enabledFeatures = useFeatureToggle();
  const isOccupancyEnabled = useAppSelector(getIsOccupancyEnabled);
  const bucket = useRefreshBucket(refreshClock);

  const building = useHaNBuilding(bucket);
  const floor = useHaNFloor({ session: haNSession, buildingId: building.buildingId });
  const mobileView = useHaNMobileView({ session: haNSession, buildingId: building.buildingId });

  // Moves the page's map view, for handlers anywhere on the page. It's `null` until the floor's map is built.
  const mapViewApiRef = useRef<MapViewApi>(null);

  // Nothing loads until the building is settled, so a late reservation doesn't load the default building first
  const loadingBuildingId = building.isSettled ? building.buildingId : null;

  // Each tab loads only what it shows. The facilities tab also shows the rooms and areas of the tabs that are
  // available, to open their details. The building's resources hold the facilities, and load once.
  const availableTabs = useAvailableTabs();
  const showsRooms = availableTabs.includes("rooms") && (tab === "rooms" || tab === "facilities");
  const showsAreas =
    availableTabs.includes("workspaces") && (tab === "workspaces" || tab === "facilities" || tab === "people");
  const loadsOccupancy = showsAreas && isOccupancyEnabled;

  const rooms = useRoomsTimeWindow({
    session: haNSession,
    buildingId: loadingBuildingId,
    floorId: floor.floorId,
    bucket,
    enabled: showsRooms,
  });

  useOccupancyLoader({ buildingId: loadingBuildingId, enabled: loadsOccupancy, bucket });
  useTypologyLoader(loadingBuildingId);
  useBuildingResourcesLoader(loadingBuildingId);

  const people = useBuildingWorkspaceReservations({
    buildingId: loadingBuildingId,
    enabled: tab === "people" || (isSearching && availableTabs.includes("people")),
    bucket,
  });

  const buildingId = building.buildingId ?? "";

  // A refresh keeps the previous states, so only a first load has none while loading
  const isOccupancyLoading = useAppSelector(state => {
    const occupancy = getWorkspaceOccupancyForBuildingId(state, buildingId);

    return !occupancy || (occupancy.status === "loading" && occupancy.states.length === 0);
  });

  const isResourcesLoading = useAppSelector(state => {
    const status = getBuildingResources(state, buildingId)?.status;

    return status !== "loaded" && status !== "failed";
  });

  // Whether the tab still waits for the first data it draws. Background refreshes keep older data on screen, so they
  // don't count.
  const getIsTabLoading = () => {
    if (!building.isSettled) return true;

    switch (tab) {
      case "rooms":
        return rooms.status === "loading";
      case "workspaces":
        return loadsOccupancy && isOccupancyLoading;
      case "people":
        return people.status === "loading";
      case "facilities":
        return isResourcesLoading;
    }
  };

  const isTabLoading = getIsTabLoading();

  const isSensorDataLoading = tab === "people" && loadsOccupancy && isOccupancyLoading;

  const filterData = useHereAndNowFilterData({
    buildingId,
    floorId: floor.floorId,
    enabledFeatures,
    startDateTime: rooms.roomDateTimes.startDateTime,
    endDateTime: rooms.roomDateTimes.endDateTime,

    getDeskAmenityIcon,
    getRoomEquipementIcon,
  });

  // Every room, area and facility in the building, on all floors
  const resources = useAppSelector(state => getBuildingResources(state, buildingId)?.resources ?? NO_RESOURCES);
  const facilities = resources.filter(isFacility);

  const usageStatsPerFloor = useUsageStats({
    buildingId,
    type: USAGE_STATS_TYPE_BY_TAB[tab],
    roomRequestScope: rooms.roomDateTimes,
  });

  return {
    bucket,
    building,
    facilities,
    filterData,
    floor,
    isResourcesLoading,
    isSensorDataLoading,
    isTabLoading,
    mapViewApiRef,
    mobileView,
    people,
    resources,
    rooms,
    showsAreas,
    showsRooms,
    tab,
    usageStatsPerFloor,
  };
};

export type HaNContextValue = ReturnType<typeof useHaNContextValue>;

export const HaNContext = createContext<HaNContextValue | null>(null);
